import { useCallback } from "react";
import { useEntityLevelCustomAttributes } from "./useEntityLevelCustomAttributes";
import { useTaskBoardsQuery } from "./useTaskBoardsQuery";

interface UseTaskStatusesGettersOptions {
  entityId: string;
}

/**
 * Factories and functions that return entity/board-level statuses by their ID.
 * Designed to be used internally by the `useTaskStatuses` hook but can also be
 * used by components which work outside of the board context.
 */
export const useTaskStatusesGetters = ({
  entityId,
}: UseTaskStatusesGettersOptions) => {
  const { getBoardByIdFromCache } = useTaskBoardsQuery({ entityId });

  const { entityLevelCustomStatuses } = useEntityLevelCustomAttributes({
    entityId,
  });

  const getStatusesByBoard = useCallback(
    (boardId: string | null) =>
      boardId
        ? getBoardByIdFromCache(boardId)?.customStatuses || []
        : entityLevelCustomStatuses,
    [entityLevelCustomStatuses, getBoardByIdFromCache]
  );

  const produceGetStatusById = useCallback(
    (boardId: string | null) => {
      const statuses = getStatusesByBoard(boardId);
      const map = new Map(statuses.map((status) => [status.id, status]));

      return (statusId: string) => map.get(statusId);
    },
    [getStatusesByBoard]
  );

  const getStatusByBoard = useCallback(
    (boardId: string | null, statusId: string) => {
      const getStatusById = produceGetStatusById(boardId);
      return getStatusById(statusId);
    },
    [produceGetStatusById]
  );

  return {
    getStatusesByBoard,
    produceGetStatusById,
    getStatusByBoard,
  };
};
